import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { ClickAwayListener, Tooltip } from '@mui/material';
import { StatusCard } from 'components';
import { authUtilizationPeriodEnums, authUtilizationStateEnums, authUtilizationStatusEnums } from 'utils';
import {
    AVAILABLE_THEME,
    ON_TARGET_MIN,
    STATUS_THEME,
    buildMonths,
    buildWeekTicks,
    dateToPercent,
    formatUnits,
    renderClientShortName,
    renderUnits,
} from './constants';

/** Unclamped position — term bars and gaps can start/end outside the range and are clipped by the track. */
const rawPercent = (date, range, endOfDay = false) =>
    ((moment(date).startOf('day').diff(range.start, 'days') + (endOfDay ? 1 : 0)) / range.totalDays) * 100;

const spanStyle = (startDate, endDate, range) => {
    const left = rawPercent(startDate, range);
    return { left: `${left}%`, width: `${rawPercent(endDate, range, true) - left}%` };
};

const StatusIcon = ({ status }) => {
    if (status === authUtilizationStatusEnums.OVER) return <span className="auth-util-arrow">▲</span>;
    if (status === authUtilizationStatusEnums.UNDER) return <span className="auth-util-arrow">▼</span>;
    return <span className="auth-util-dot" />;
};

const FlagIcon = () => (
    <svg width="10" height="11" viewBox="0 0 10 11" fill="none" aria-hidden="true">
        <path d="M1 10.5V1M1 1h7l-1.5 2.5L8 6H1" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="currentColor" />
    </svg>
);

const WarningIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/** Start year only when it differs from the end year: "Jul 1 – Jul 31, 2027", "Dec 3, 2025 – Dec 28, 2027". */
const formatRange = (startDate, endDate) => {
    const sameYear = moment(startDate).year() === moment(endDate).year();
    return `${moment(startDate).format(sameYear ? 'MMM D' : 'MMM D, YYYY')} – ${moment(endDate).format('MMM D, YYYY')}`;
};
const formatSlashRange = (startDate, endDate) => `${moment(startDate).format('MM/DD/YYYY')} – ${moment(endDate).format('MM/DD/YYYY')}`;

/** `unitSize` is minutes per unit. */
const formatHours = (units, unitSize) => {
    if (!unitSize) return null;
    const hours = (units * unitSize) / 60;
    return `${Number(hours.toFixed(1))} hours`;
};

/** White card instead of MUI's default dark tooltip, per design. */
const CARD_TOOLTIP_PROPS = {
    tooltip: {
        sx: {
            background: '#fff',
            color: '#101828',
            padding: 0,
            maxWidth: 360,
            borderRadius: '12px',
            border: '1px solid #EAECF0',
            boxShadow: '0 12px 16px -4px rgba(16,24,40,0.08), 0 4px 6px -2px rgba(16,24,40,0.03)',
            // MUI spaces the tooltip from its anchor with a margin on the anchor side, so the card's measured size
            // depended on its placement (+14px tall when top/bottom, +14px wide when left/right). With the flip
            // fallbacks below, a card that only just fits flipped top → right (now shorter, so top fits) → top …
            // forever — MUI re-runs Popper after every placement change, and the card vibrated. The gap comes from
            // the `offset` modifier instead, so the size is the same in every placement.
            margin: '0 !important',
        },
    },
    arrow: { sx: { color: '#fff', '&:before': { border: '1px solid #EAECF0' } } },
    /**
     * The card is ~450px tall, so "top" alone clips its header on the upper rows. Popper's defaults measure against
     * the timeline's scroll container (the card is portaled, so that box is irrelevant) and never slide the card
     * vertically. Instead: flip against the viewport only — top, then bottom, then beside the chip — and let it
     * slide along both axes (untethered) so it always lands fully on screen. `boundary: []` leaves just Popper's
     * root boundary (the viewport); `document.documentElement` is wrong — <html> can be shorter than the window, so
     * cards for lower rows were clamped up to the top of the screen, far from the clicked bar.
     */
    popper: {
        modifiers: [
            { name: 'offset', options: { offset: [0, 14] } },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['bottom', 'right', 'left'],
                    boundary: [],
                    padding: 8,
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: [],
                    altAxis: true,
                    tether: false,
                    padding: 8,
                },
            },
        ],
    },
};

const periodTitle = (auth, period) => {
    if (auth?.period === authUtilizationPeriodEnums.MONTH) return `Month of ${formatSlashRange(period?.startDate, period?.endDate)}`;
    return `Week of ${formatSlashRange(period?.startDate, period?.endDate)}`;
};

/**
 * Booking past the authorized total is the headline problem — it is what makes the status Overutilized — so it
 * replaces the pace note. Pace counts completed units only and could otherwise say "Behind pace" on an
 * Overutilized card.
 */
const PaceNote = ({ projection, endDate, overall }) => {
    if (overall?.usedUnits > overall?.authorizedUnits) {
        return (
            <div className="auth-util-card-note auth-util-card-note--over">
                <p className="auth-util-card-note-title">
                    <WarningIcon /> Over authorized units
                </p>
                <p>
                    <b>{formatUnits(overall.usedUnits)}</b> of {formatUnits(overall.authorizedUnits)} units booked ·{' '}
                    {formatUnits(overall.usedUnits - overall.authorizedUnits)} over
                </p>
            </div>
        );
    }
    if (!projection) return null;
    if (projection.ahead) {
        return (
            <div className="auth-util-card-note auth-util-card-note--over">
                <p className="auth-util-card-note-title">
                    <WarningIcon /> Ahead of pace
                </p>
                <p>
                    Projected to run out <b>{moment(projection.runOutDate).format('MMM D')}</b> · {projection.daysEarly} days before
                    authorization end
                </p>
            </div>
        );
    }
    if (projection.projectedPercent < ON_TARGET_MIN) {
        return (
            <div className="auth-util-card-note auth-util-card-note--under">
                <p className="auth-util-card-note-title">
                    <WarningIcon /> Behind pace
                </p>
                <p>
                    Projected to use <b>{projection.projectedPercent}%</b> of authorized units by {moment(endDate).format('MMM D')}
                </p>
            </div>
        );
    }
    return null;
};

const PeriodTooltip = ({ client, auth, period }) => {
    const statusTheme = STATUS_THEME[auth?.status];
    const periodTheme = STATUS_THEME[period?.status];
    const overall = auth?.overall || {};
    const hours = formatHours(overall.authorizedUnits, auth?.unitSize);
    const completedPercent = Math.min(100, (overall.completedUnits / (overall.authorizedUnits || 1)) * 100);
    const totalPercent = Math.max(completedPercent, Math.min(100, overall.utilization || 0));
    // Only a chip has a period of its own. An entire-term week has no limit, so a week block would have nothing
    // to compare against — the term bar's card shows the overall numbers and the pace note only.
    const block = auth?.hasPeriodBreakdown ? period : null;

    return (
        <div className="auth-util-card">
            <div className="auth-util-card-head">
                <div>
                    <p className="auth-util-card-title">
                        {renderClientShortName(client)} <span>· {auth?.serviceCode}</span>
                    </p>
                    <p className="auth-util-card-sub">{[auth?.authId, auth?.payer, auth?.staffName].filter(Boolean).join(' · ')}</p>
                </div>
                {auth?.cancelled || auth?.expired ? (
                    <InactiveBadge auth={auth} />
                ) : (
                    statusTheme && (
                        <span
                            className="auth-util-card-badge"
                            style={{ color: statusTheme.text, background: statusTheme.bg, borderColor: statusTheme.border }}
                        >
                            Overall: {statusTheme.label}
                        </span>
                    )
                )}
            </div>

            <div className="auth-util-card-section">
                <div className="auth-util-card-row">
                    <span>Period</span>
                    <b>{formatSlashRange(auth?.startDate, auth?.endDate)}</b>
                </div>
                <div className="auth-util-card-row">
                    <span>Authorized</span>
                    <b>
                        {formatUnits(overall.authorizedUnits)} units{hours && <em> · {hours}</em>}
                    </b>
                </div>

                <p className="auth-util-card-label">Overall utilization</p>
                {/* Same split as the term bar: completed solid, scheduled striped after it. */}
                <div className="auth-util-card-bar" style={{ '--term-solid': statusTheme?.solid || '#98A2B3' }}>
                    <span className="auth-util-card-bar-completed" style={{ width: `${completedPercent}%` }} />
                    <span className="auth-util-card-bar-scheduled" style={{ width: `${totalPercent - completedPercent}%` }} />
                </div>
                <div className="auth-util-card-row">
                    <span className="auth-util-card-strong">
                        {formatUnits(overall.usedUnits)} of {formatUnits(overall.authorizedUnits)} units used
                    </span>
                    <b style={{ color: statusTheme?.text }}>{overall.utilization}%</b>
                </div>
                <p className="auth-util-card-muted">
                    {overall.usedUnits > overall.authorizedUnits
                        ? `${formatUnits(overall.usedUnits - overall.authorizedUnits)} units over`
                        : `${formatUnits(overall.remainingUnits)} units remaining`}{' '}
                    · {formatUnits(overall.completedUnits)} completed, {formatUnits(overall.scheduledUnits)} scheduled
                </p>

                {block && (
                    <>
                        <div className="auth-util-card-label-row">
                            <p className="auth-util-card-label">
                                {block?.state === authUtilizationStateEnums.CURRENT ? 'Current period' : 'Period'}
                            </p>
                            {/* The clicked week/month's own status — matches the chip's color, unlike the overall badge above. */}
                            {periodTheme && (
                                <span
                                    className="auth-util-card-badge auth-util-card-badge--small"
                                    style={{ color: periodTheme.text, background: periodTheme.bg, borderColor: periodTheme.border }}
                                >
                                    {periodTheme.label}
                                </span>
                            )}
                        </div>
                        <p className="auth-util-card-period">{periodTitle(auth, block)}</p>
                        <div className="auth-util-card-row">
                            <span>Completed</span>
                            <b>{formatUnits(block?.completedUnits)} units</b>
                        </div>
                        <div className="auth-util-card-row">
                            <span>Scheduled</span>
                            <b>{formatUnits(block?.scheduledUnits)} units</b>
                        </div>
                        <div className="auth-util-card-row auth-util-card-total">
                            <span>Total</span>
                            <b>
                                {formatUnits(block?.completedUnits + block?.scheduledUnits)} of {formatUnits(block?.authorizedUnits)} units
                            </b>
                        </div>
                        <div className="auth-util-card-row">
                            <span>Utilization</span>
                            <b style={{ color: periodTheme?.text }}>{block?.utilization === null ? '—' : `${block?.utilization}%`}</b>
                        </div>
                    </>
                )}
            </div>

            <PaceNote projection={auth?.projection} endDate={auth?.endDate} overall={overall} />
        </div>
    );
};

/**
 * Info boxes on the timeline open on click (click again to close) and close on click-away or Escape — not on hover
 * (the cancelled bar is the exception: a one-line hover tooltip). `atCursor` anchors the box at the click point
 * instead of the element's centre: term bars and gaps can be wider than the screen, so centring on them could put the box off screen. The point is kept
 * relative to the element, so the box stays with it when the page scrolls.
 */
const ClickTooltip = ({ children, atCursor, slotProps, ...tooltipProps }) => {
    const [open, setOpen] = useState(false);
    // Kept after closing: the box fades out over a transition, and dropping the point mid-fade made it jump to the
    // element's centre (far away on a wide term bar) before disappearing.
    const [click, setClick] = useState(null);
    const close = () => setOpen(false);
    const toggle = (event) => {
        if (open) return close();
        const el = event.currentTarget;
        const rect = el.getBoundingClientRect();
        setClick({ el, x: event.clientX - rect.left, y: event.clientY - rect.top });
        setOpen(true);
    };
    // Memoised so Popper isn't handed a new anchor (and repositioned) on every render.
    const cursorAnchor = useMemo(
        () =>
            click && {
                getBoundingClientRect: () => {
                    const rect = click.el.getBoundingClientRect();
                    const x = rect.left + click.x;
                    const y = rect.top + click.y;
                    return { top: y, bottom: y, left: x, right: x, x, y, width: 0, height: 0 };
                },
            },
        [click]
    );

    return (
        <ClickAwayListener onClickAway={close}>
            <Tooltip
                {...tooltipProps}
                open={open}
                onClose={close}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                slotProps={
                    atCursor && cursorAnchor ? { ...slotProps, popper: { ...slotProps?.popper, anchorEl: cursorAnchor } } : slotProps
                }
            >
                {React.cloneElement(children, { onClick: toggle })}
            </Tooltip>
        </ClickAwayListener>
    );
};

/** Cancelled wins over expired: a cancelled auth never ran, an expired one ran to its end date. */
const InactiveBadge = ({ auth }) => <span className="auth-util-inactive-badge">{auth?.cancelled ? 'Cancelled' : 'Expired'}</span>;

/** A cancelled service has no usage to show (cancelling is refused once units are used) — just its striped span. */
const CancelledBar = ({ auth, range }) => (
    <Tooltip
        title={`Cancelled${auth?.cancelledReason ? ` · ${auth.cancelledReason}` : ''} (${formatRange(auth?.startDate, auth?.endDate)})`}
        placement="top"
        arrow
        followCursor
    >
        <div className="auth-util-cancelled" style={spanStyle(auth?.startDate, auth?.endDate, range)}>
            Cancelled
        </div>
    </Tooltip>
);

/**
 * A period with fewer visible days than this is a sliver at the range edge (e.g. one day of a month) — too narrow
 * to read, so it isn't drawn. Its units still count in the overall utilization on the info card.
 */
const MIN_CHIP_DAYS = 3;
const visibleDays = (period, range) =>
    moment.min(moment(period?.endDate), range.end).diff(moment.max(moment(period?.startDate), range.start), 'days') + 1;

/**
 * Usage booked before the first visible day. The info card's totals include it but no chip shows it, so without
 * this a heavily used auth can look empty. Sits on the sub-line, not the track, so it never covers a chip.
 */
const EarlierUnits = ({ units, range }) => (
    <ClickTooltip
        title={`${formatUnits(units)} units booked before ${range.start.format('MMM D, YYYY')} — use ‹ to see earlier months`}
        placement="top"
        arrow
    >
        <span className="auth-util-earlier">
            ◀ {formatUnits(units)}u before {range.start.format('MMM D')}
        </span>
    </ClickTooltip>
);

const PeriodChip = ({ client, auth, period, range }) => {
    const isAvailable = period?.utilization === null;
    const theme = isAvailable ? AVAILABLE_THEME : STATUS_THEME[period?.status];
    const isScheduled = period?.state === authUtilizationStateEnums.SCHEDULED;
    const left = dateToPercent(period?.startDate, range);
    const width = dateToPercent(period?.endDate, range, true) - left;

    return (
        <ClickTooltip
            title={<PeriodTooltip client={client} auth={auth} period={period} />}
            placement="top"
            arrow
            slotProps={CARD_TOOLTIP_PROPS}
        >
            <div
                className={`auth-util-chip${isScheduled ? ' auth-util-chip--scheduled' : ''}${
                    isAvailable ? ' auth-util-chip--available' : ''
                }`}
                style={{
                    // 2px gutter on each side so consecutive periods read as separate chips.
                    left: `calc(${left}% + 2px)`,
                    width: `calc(${width}% - 4px)`,
                    '--chip-text': theme?.text,
                    '--chip-border': theme?.border,
                    '--chip-bg': theme?.bg,
                    '--chip-bar': theme?.bar,
                    '--chip-dot': theme?.dot,
                }}
            >
                <span className="auth-util-chip-label">
                    {isAvailable ? (
                        '– –'
                    ) : (
                        <>
                            <StatusIcon status={period?.status} />
                            {period?.utilization}%
                        </>
                    )}
                </span>
                {!isAvailable && (
                    <span className="auth-util-chip-bar">
                        <span style={{ width: `${Math.min(100, period?.utilization)}%` }} />
                    </span>
                )}
            </div>
        </ClickTooltip>
    );
};

/** Room kept for the label at the visible right edge — fits "▼ 100% completed · 100% scheduled of 1000u ⚑". */
const TERM_LABEL_WIDTH = 280;

/**
 * The fill is a share of the whole term, so when the range shows only part of the bar the fill's end — and the
 * label after it — can be off screen, and a visible stretch that is all fill reads as 100% used. The label is
 * therefore clamped into the bar's visible part: after the fill when that is on screen, else pinned to the
 * nearer visible edge (on a white pill, so it reads over the fill too).
 */
const termLabelLeft = (period, range, fill) => {
    const barLeft = rawPercent(period?.startDate, range);
    const barWidth = rawPercent(period?.endDate, range, true) - barLeft;
    if (barWidth <= 0) return `${fill}%`;
    const visibleStart = ((Math.max(0, barLeft) - barLeft) / barWidth) * 100;
    const visibleEnd = ((Math.min(100, barLeft + barWidth) - barLeft) / barWidth) * 100;
    return `clamp(${visibleStart}%, ${fill}%, calc(${visibleEnd}% - ${TERM_LABEL_WIDTH}px))`;
};

/**
 * The bar is the whole term, so a fill that is N% long ends N% of the way through the term. Only completed
 * units are solid: comparing where they end with the today line is exactly the pace the status measures
 * (ends before the line = behind, after = ahead). Scheduled units follow as a striped, not-yet-done segment —
 * they are future bookings, so counting them in the solid fill made an underutilized auth look ahead of pace.
 */
const TermBar = ({ client, auth, period, range }) => {
    const theme = STATUS_THEME[period?.status] || STATUS_THEME[authUtilizationStatusEnums.ON_TARGET];
    const authorized = period?.authorizedUnits || 0;
    const percentOf = (units) => (authorized ? Math.min(100, (units / authorized) * 100) : 0);
    const completed = percentOf(period?.completedUnits || 0);
    const total = percentOf((period?.completedUnits || 0) + (period?.scheduledUnits || 0));

    return (
        <ClickTooltip
            title={<PeriodTooltip client={client} auth={auth} period={period} />}
            placement="top"
            arrow
            atCursor
            slotProps={CARD_TOOLTIP_PROPS}
        >
            <div
                className="auth-util-term"
                style={{ ...spanStyle(period?.startDate, period?.endDate, range), '--term-light': theme.light }}
            >
                <div className="auth-util-term-fill" style={{ width: `${completed}%`, background: theme.solid }} />
                {total > completed && (
                    <div className="auth-util-term-scheduled" style={{ width: `${total - completed}%`, '--term-solid': theme.solid }} />
                )}
                <div className="auth-util-term-label" style={{ color: theme.text, left: termLabelLeft(period, range, total) }}>
                    <StatusIcon status={period?.status} />
                    {total > completed
                        ? `${Math.round(completed)}% completed · ${Math.round(total - completed)}% scheduled of ${formatUnits(authorized)}u`
                        : `${Math.round(completed)}% of ${formatUnits(authorized)}u`}
                    <FlagIcon />
                </div>
            </div>
        </ClickTooltip>
    );
};

const GapMarker = ({ gap, range }) => (
    <ClickTooltip
        title={`No active authorization for ${gap.days} days (${formatRange(gap.startDate, gap.endDate)})`}
        placement="top"
        arrow
        atCursor
    >
        <div className="auth-util-gap" style={spanStyle(gap.startDate, gap.endDate, range)}>
            <WarningIcon />
            gap {gap.days}d
        </div>
    </ClickTooltip>
);

const AuthRow = ({ client, auth, range }) => {
    const theme = STATUS_THEME[auth?.status];
    // Without a per-period breakdown, weekly/monthly auths can only be shown against their term totals.
    const isTerm = auth?.period === authUtilizationPeriodEnums.TERM || !auth?.hasPeriodBreakdown;

    return (
        <div className={`auth-util-row${auth?.expired ? ' auth-util-row--expired' : ''}`}>
            <div className="auth-util-client-col auth-util-auth-info">
                <p className="auth-util-auth-title">
                    <b>
                        {auth?.serviceCode} · {renderUnits(auth)}
                    </b>
                    <span className="auth-util-payer" title={auth?.payer}>
                        {auth?.payer}
                    </span>
                    {auth?.cancelled || auth?.expired ? (
                        <InactiveBadge auth={auth} />
                    ) : (
                        <span/>
                        // <span className="auth-util-status-dot" style={{ background: theme?.dot }} title={theme?.label} />
                    )}
                </p>
                {/* One line: auth # · provider truncates so the earlier-units label never wraps it onto a second line. */}
                <p className="auth-util-auth-sub">
                    <span className="auth-util-auth-sub-text" title={`${auth?.authId} · ${auth?.staffName}`}>
                        {auth?.authId} · {auth?.staffName}
                        {auth?.staffCredential ? `, ${auth.staffCredential}` : ''}
                    </span>
                    {/* A cancelled auth has nothing to account for, so no "before" label even if stray appointments exist. */}
                    {!auth?.cancelled && auth?.earlierUnits > 0 && <EarlierUnits units={auth.earlierUnits} range={range} />}
                </p>
            </div>
            <div className="auth-util-track">
                {auth?.gapBefore && <GapMarker gap={auth.gapBefore} range={range} />}
                {auth?.cancelled && <CancelledBar auth={auth} range={range} />}
                {!auth?.cancelled &&
                    auth?.periods?.map((period) =>
                        isTerm ? (
                            <TermBar key={period.startDate} client={client} auth={auth} period={period} range={range} />
                        ) : visibleDays(period, range) < MIN_CHIP_DAYS ? null : (
                            <PeriodChip key={period.startDate} client={client} auth={auth} period={period} range={range} />
                        )
                    )}
            </div>
        </div>
    );
};

/**
 * `children` render in the body in place of the rows (skeleton / error / empty state), so the header stays up
 * without rows.
 */
export const UtilizationTimeline = ({ clients, range, children }) => {
    const [collapsed, setCollapsed] = useState({});
    const months = useMemo(() => buildMonths(range), [range]);
    const ticks = useMemo(() => buildWeekTicks(range), [range]);
    const today = moment().startOf('day');
    const todayVisible = !today.isBefore(range.start) && !today.isAfter(range.end);
    const todayLeft = dateToPercent(today, range) + 50 / range.totalDays;
    const hasRows = !!clients?.length;
    const toggle = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

    return (
        <div className="auth-util-table">
            <div className="auth-util-head">
                <div className="auth-util-client-col auth-util-head-label">CLIENT</div>
                <div className="auth-util-track auth-util-head-track">
                    {months.map((month) => (
                        <div key={month.key} className="auth-util-month" style={{ left: `${month.left}%`, width: `${month.width}%` }}>
                            <span>{month.width > 8 ? month.label : ''}</span>
                        </div>
                    ))}
                    {ticks.map((tick) => (
                        <span key={tick.key} className="auth-util-tick" style={{ left: `${tick.left}%` }}>
                            {tick.label}
                        </span>
                    ))}
                </div>
            </div>

            <div className="auth-util-body">
                {hasRows && (
                    <>
                        <div className="auth-util-overlay">
                            {months.slice(1).map((month) => (
                                <span key={month.key} className="auth-util-month-line" style={{ left: `${month.left}%` }} />
                            ))}
                        </div>
                        <div className="auth-util-overlay auth-util-overlay--front">
                            {todayVisible && (
                                <div className="auth-util-today" style={{ left: `${todayLeft}%` }}>
                                    <span className="auth-util-today-label">TODAY · {today.format('MMM D')}</span>
                                </div>
                            )}
                        </div>
                    </>
                )}
                {!hasRows && children}

                {clients?.map((client) => (
                    <React.Fragment key={client.id}>
                        <div className="auth-util-row auth-util-group-row">
                            <button type="button" className="auth-util-client-col auth-util-group-toggle" onClick={() => toggle(client.id)}>
                                <span className={`auth-util-chevron${collapsed[client.id] ? ' auth-util-chevron--collapsed' : ''}`}>⌄</span>
                                {renderClientShortName(client)}
                                {client?.status && (
                                    <span className="auth-util-client-status">
                                        <StatusCard status={client.status} />
                                    </span>
                                )}
                                {client?.code && <span className="auth-util-client-code">({client.code})</span>}
                            </button>
                            <div className="auth-util-track" />
                        </div>
                        {!collapsed[client.id] &&
                            client.services?.map((auth) => <AuthRow key={auth.id} client={client} auth={auth} range={range} />)}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};
